import { resolveStorageUrl, type ImageRefInput } from "@/lib/storage/public-url"

/**
 * MECHANISM ONLY. The (width, quality) numbers a screen should ask for are
 * POLICY and live in `lib/images/presets.ts` — including `CARD`, the successor
 * of the `LISTING_CARD_IMAGE_WIDTH` / `_QUALITY` constants that used to sit
 * here. Different rates of change: this rewriter has not moved since it was
 * written, the sizes move with the design.
 *
 * Prefer `imagePresetUrl(raw, PRESET)` from `lib/images/presets.ts` over calling
 * this directly with literals — a numeric literal here is a new cache variant
 * for every image in the catalogue.
 *
 * @see lib/images/presets.ts
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §8.2
 */

/**
 * Rewrites a Supabase public-storage URL to the CDN image-transform endpoint
 * so thumbnails download a resized variant instead of the full upload.
 *
 * This is the widest READ FUNNEL in the app (~19 files, incl. `ListingImage`),
 * which is why the relative-path resolve happens HERE rather than at every
 * render point: `url` may be a bucket-relative path (`listings/foo.jpg`) now
 * that the DB is migrating off absolute Supabase URLs. Resolve runs BEFORE the
 * marker search — otherwise `indexOf(marker) === -1` returns the bare path
 * unchanged and the browser requests it against the SITE domain (404, silent).
 *
 * `resolveStorageUrl` is idempotent and passes absolute / `blob:` / `data:` /
 * `/local.svg` values straight through, so existing callers are unaffected.
 *
 * Accepts `RawImageRef` as well as `string` — this IS the sanctioned exit from
 * the brand (it resolves), so a value read straight out of one of the 7 SoT
 * columns can be handed in without an escape hatch.
 *
 * @see lib/storage/url.ts
 * @see packages/contract/src/raw-image-ref.ts
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
 */
export function toSupabasePublicImageThumbUrl(
  raw: ImageRefInput,
  width: number,
  quality = 60,
  resize: "cover" | "contain" = "cover",
  height?: number
): string {
  // Resolve FIRST, then the empty guard: `resolveStorageUrl("")` is `""`, so
  // behaviour is identical while the guard no longer needs `raw` to be a
  // `string` at the type level.
  const url = resolveStorageUrl(raw)
  if (!url) return url
  const normalizedWidth = Math.max(1, Math.round(width))
  const normalizedHeight = height != null ? Math.max(1, Math.round(height)) : undefined
  const normalizedQuality = Math.max(1, Math.min(100, Math.round(quality)))
  // With only `width` given, the render endpoint under `resize=cover` fills
  // width x ORIGINAL height (crops the sides to a vertical sliver — verified
  // 2026-07-13: 1320x1602 + width=200&resize=cover -> 200x1602). `contain`
  // with a single dimension is the aspect-preserving downscale; only use
  // `cover` when BOTH dimensions are provided.
  const effectiveResize = normalizedHeight != null ? resize : "contain"
  const marker = "/storage/v1/object/public/"
  const renderMarker = "/storage/v1/render/image/public/"

  if (url.includes(renderMarker)) {
    try {
      const u = new URL(url)
      u.searchParams.set("width", String(normalizedWidth))
      // DELETE, don't skip: re-applying a height-less preset over a URL that
      // already carries `height` would otherwise leave the OLD height in place
      // and emit `width=384&height=<stale>&quality=75&resize=contain` — a box
      // fit against a height nobody asked for. Re-application has to be a full
      // overwrite of the size triple, or it is not idempotent.
      if (normalizedHeight != null) {
        u.searchParams.set("height", String(normalizedHeight))
      } else {
        u.searchParams.delete("height")
      }
      u.searchParams.set("quality", String(normalizedQuality))
      u.searchParams.set("resize", effectiveResize)
      return u.toString()
    } catch {
      return url
    }
  }

  const idx = url.indexOf(marker)
  if (idx === -1) return url

  const base = url.slice(0, idx)
  const path = url.slice(idx + marker.length)
  const rendered = `${base}${renderMarker}${path}`

  try {
    const u = new URL(rendered)
    u.searchParams.set("width", String(normalizedWidth))
    if (normalizedHeight != null) u.searchParams.set("height", String(normalizedHeight))
    u.searchParams.set("quality", String(normalizedQuality))
    u.searchParams.set("resize", effectiveResize)
    return u.toString()
  } catch {
    return rendered
  }
}


